import { placeTip } from './lib/tipPlacement.ts';

/**
 * Puts a chart's hover readout where it hides nothing that matters.
 *
 * The geometry rule lives in `lib/tipPlacement`; this is the DOM half — reading
 * the real sizes and writing the result. Shared by every chart on the page so
 * two of them sitting in the same panel behave the same way.
 *
 * Coordinates come from `getScreenCTM`, not a box ratio: each chart has a
 * viewBox and a fixed height, so it letterboxes in a narrow panel and a ratio
 * would drift.
 *
 * Call after the tip is filled and unhidden — placement needs its real size.
 */
export function positionChartTip(
  svg: SVGSVGElement,
  wrap: HTMLElement,
  tip: HTMLElement,
  /** Crosshair position, in the SVG's own units. */
  tx: number,
  /** The band to step off, in SVG units — usually the plot the pointer is in. */
  band: { top: number; bottom: number },
): void {
  const ctm = svg.getScreenCTM();
  if (!ctm || ctm.d === 0) return;

  const wrapBox = wrap.getBoundingClientRect();
  const toLocalY = (y: number) => ctm.f + y * ctm.d - wrapBox.top;

  const { left, top } = placeTip({
    crosshairX: ctm.e + tx * ctm.a - wrapBox.left,
    bandTop: toLocalY(band.top),
    bandBottom: toLocalY(band.bottom),
    wrapWidth: wrapBox.width,
    wrapHeight: wrapBox.height,
    tipWidth: tip.offsetWidth,
    tipHeight: tip.offsetHeight,
  });

  // Positioned by its own edge, so the centring transform has to go.
  tip.style.transform = 'none';
  tip.style.left = `${left}px`;
  tip.style.top = `${top}px`;
}
